import { useCallback, useRef, useEffect, useState } from 'react';
import * as fabric from 'fabric';
import { useDocumentStore } from '../../stores/documentStore';
import { useAnnotationStore } from '../../stores/annotationStore';
import { useUIStore } from '../../stores/uiStore';
import { Toolbar } from './Toolbar';
import { Sidebar } from './Sidebar';
import { StatusBar } from './StatusBar';
import { TabBar } from './TabBar';
import { PDFViewer } from '../viewer/PDFViewer';
import { SearchPanel } from '../viewer/SearchPanel';
import { MergeDialog } from '../document/MergeDialog';
import { SplitDialog } from '../document/SplitDialog';
import { PageOrganizer } from '../document/PageOrganizer';
import { ConvertDialog } from '../document/ConvertDialog';
import { CompressDialog } from '../document/CompressDialog';
import { OCRDialog } from '../tools/OCRDialog';
import { SignaturePad } from '../tools/SignaturePad';
import { PropertiesPanel } from '../document/PropertiesPanel';
import { AdminPanel } from '../admin/AdminPanel';
import { readFileAsArrayBuffer, isPDFFile, generateId } from '../../utils/fileUtils';
import { pdfEngine } from '../../core/PDFEngine';
import type { PDFDocumentInfo } from '../../stores/documentStore';
import './AppShell.css';

export function AppShell() {
  const { documents, activeDocumentId, addDocument, setCurrentPage, setTotalPages, setIsLoading, setLoadingProgress } = useDocumentStore();
  const {
    sidebarVisible,
    searchPanelVisible,
    mergeDialogOpen,
    splitDialogOpen,
    pageOrganizerOpen,
    convertDialogOpen,
    compressDialogOpen,
    ocrDialogOpen,
    signatureDialogOpen,
    propertiesDialogOpen,
    adminDialogOpen,
    closeDialog,
  } = useUIStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const activeDocument = documents.find((d) => d.id === activeDocumentId);

  const handleOpenFile = useCallback(async (file: File) => {
    if (!isPDFFile(file)) {
      useUIStore.getState().addToast({ type: 'error', message: 'Please select a PDF file' });
      return;
    }

    setIsLoading(true);
    setLoadingProgress(0);

    try {
      const arrayBuffer = await readFileAsArrayBuffer(file);
      const doc = await pdfEngine.loadDocument(arrayBuffer.slice(0), (progress) => {
        setLoadingProgress(progress);
      });

      const docInfo: PDFDocumentInfo = {
        id: generateId(),
        filename: file.name,
        pageCount: doc.numPages,
        fileSize: file.size,
        file,
        arrayBuffer,
        isSaved: false,
        lastModified: Date.now(),
      };

      addDocument(docInfo);
      setTotalPages(doc.numPages);
      setCurrentPage(1);

      useUIStore.getState().addToast({
        type: 'success',
        message: `Opened "${file.name}" (${doc.numPages} pages)`,
      });
    } catch (error) {
      console.error('Failed to open PDF:', error);
      useUIStore.getState().addToast({
        type: 'error',
        message: `Failed to open "${file.name}". The file may be corrupted or password protected.`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [addDocument, setCurrentPage, setTotalPages, setIsLoading, setLoadingProgress]);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files[0]) {
        handleOpenFile(files[0]);
      }
      e.target.value = '';
    },
    [handleOpenFile]
  );

  const triggerFileOpen = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files[0]) {
        handleOpenFile(files[0]);
      }
    },
    [handleOpenFile]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if focus is in an input or textarea
      const targetTag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (targetTag === 'input' || targetTag === 'textarea') {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.code) {
          case 'KeyO':
            e.preventDefault();
            triggerFileOpen();
            break;
          case 'KeyF':
            e.preventDefault();
            useUIStore.getState().toggleSearchPanel();
            break;
          case 'Equal':
          case 'NumpadAdd':
            e.preventDefault();
            useDocumentStore.getState().zoomIn();
            break;
          case 'Minus':
          case 'NumpadSubtract':
            e.preventDefault();
            useDocumentStore.getState().zoomOut();
            break;
          case 'KeyZ':
            e.preventDefault();
            if (e.shiftKey) {
              useAnnotationStore.getState().redo();
            } else {
              useAnnotationStore.getState().undo();
            }
            break;
          case 'KeyY':
            e.preventDefault();
            useAnnotationStore.getState().redo();
            break;
        }
      }

      if (!e.ctrlKey && !e.metaKey) {
        switch (e.code) {
          case 'F11':
            e.preventDefault();
            useUIStore.getState().toggleFullscreen();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerFileOpen]);

  const handleAddSignature = useCallback(async (dataUrl: string) => {
    const { currentPage } = useDocumentStore.getState();
    const { fabricCanvases } = useAnnotationStore.getState();
    const fCanvas = fabricCanvases.get(currentPage);

    if (fCanvas) {
      const img = await fabric.FabricImage.fromURL(dataUrl);
      img.scaleToWidth(180);
      img.set({
        left: 100,
        top: 100,
        selectable: true,
      });
      fCanvas.add(img);
      fCanvas.setActiveObject(img);
      fCanvas.renderAll();
      useUIStore.getState().addToast({ type: 'success', message: 'Signature added to page!' });
    } else {
      useUIStore.getState().addToast({ type: 'warning', message: 'Please select a page first' });
    }
  }, []);

  return (
    <div
      className={`app-shell ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileInput}
        className="sr-only"
        id="file-input"
      />

      <Toolbar onOpenFile={triggerFileOpen} />

      {activeDocument && <TabBar />}

      <div className="app-shell-body">
        {sidebarVisible && activeDocument && <Sidebar />}

        <main className="app-shell-content">
          {searchPanelVisible && <SearchPanel />}
          
          {activeDocument ? (
            <PDFViewer />
          ) : (
            <WelcomeScreen onOpenFile={triggerFileOpen} />
          )}
        </main>
      </div>

      <StatusBar />

      {/* Drag overlay */}
      {isDragOver && (
        <div className="drag-overlay">
          <div className="drag-overlay-content animate-bounce-in">
            <div className="drag-icon">📄</div>
            <h3>Drop PDF file here</h3>
            <p>Release to open your document</p>
          </div>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer />

      {/* Dialogs */}
      {mergeDialogOpen && <MergeDialog onClose={() => closeDialog('merge')} />}
      {splitDialogOpen && <SplitDialog onClose={() => closeDialog('split')} />}
      {pageOrganizerOpen && <PageOrganizer onClose={() => closeDialog('pageOrganizer')} />}
      {convertDialogOpen && <ConvertDialog onClose={() => closeDialog('convert')} />}
      {compressDialogOpen && <CompressDialog onClose={() => closeDialog('compress')} />}
      {ocrDialogOpen && <OCRDialog onClose={() => closeDialog('ocr')} />}
      {signatureDialogOpen && (
        <SignaturePad
          onClose={() => closeDialog('signature')}
          onSave={handleAddSignature}
        />
      )}
      {propertiesDialogOpen && <PropertiesPanel onClose={() => closeDialog('properties')} />}
      {adminDialogOpen && <AdminPanel onClose={() => closeDialog('admin')} />}
    </div>
  );
}

/* Welcome Screen - shown when no document is open */
function WelcomeScreen({ onOpenFile }: { onOpenFile: () => void }) {
  return (
    <div className="welcome-screen">
      <div className="welcome-content animate-fade-in-up">
        <div className="welcome-logo">
          <div className="welcome-logo-icon">
            <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
              <defs>
                <linearGradient id="wg" x1="0" y1="0" x2="32" y2="32">
                  <stop offset="0%" stopColor="#6366f1"/>
                  <stop offset="100%" stopColor="#8b5cf6"/>
                </linearGradient>
              </defs>
              <rect width="32" height="32" rx="6" fill="url(#wg)"/>
              <path d="M9 7h8.5a4.5 4.5 0 0 1 0 9H13v9H9V7z" fill="white" opacity="0.95"/>
              <path d="M13 11h4a1.5 1.5 0 0 1 0 3h-4v-3z" fill="url(#wg)"/>
            </svg>
          </div>
          <h1>PDF Exchange Pro</h1>
          <p className="welcome-subtitle">Professional PDF editor in your browser</p>
        </div>

        <button className="btn btn-primary btn-lg welcome-open-btn" onClick={onOpenFile}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
            <polyline points="13 2 13 9 20 9"/>
          </svg>
          Open PDF File
        </button>

        <p className="welcome-hint">or drag & drop a PDF file anywhere</p>

        <div className="welcome-shortcuts">
          <span className="shortcut-badge">Ctrl+O</span> Open File
          <span className="shortcut-badge">Ctrl+F</span> Search
          <span className="shortcut-badge">Ctrl+±</span> Zoom
        </div>

        <div className="welcome-features">
          {[
            { icon: '✏️', label: 'Annotate', desc: 'Highlight, draw, add text' },
            { icon: '📋', label: 'Merge & Split', desc: 'Combine or separate PDFs' },
            { icon: '🔍', label: 'OCR', desc: 'Extract text from scans' },
            { icon: '✍️', label: 'Sign', desc: 'Add your signature' },
            { icon: '📝', label: 'Forms', desc: 'Fill interactive forms' },
            { icon: '🖼️', label: 'Convert', desc: 'PDF ↔ Image conversion' },
          ].map((f) => (
            <div className="welcome-feature" key={f.label}>
              <span className="welcome-feature-icon">{f.icon}</span>
              <span className="welcome-feature-label">{f.label}</span>
              <span className="welcome-feature-desc">{f.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Toast Container */
function ToastContainer() {
  const { toasts, removeToast } = useUIStore();

  if (toasts.length === 0) return null;

  const icons: Record<string, string> = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  };

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast-${toast.type}`}
          onClick={() => removeToast(toast.id)}
        >
          <span className={`toast-icon toast-icon-${toast.type}`}>{icons[toast.type]}</span>
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}
